import React, { useState, useEffect, Suspense } from 'react';
import { PageSkeleton } from './components/PageSkeleton';
import { RouteMetaUpdater } from './components/RouteMetaUpdater';
import { ThemeProvider } from './contexts/ThemeContext';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { Breadcrumb } from './components/Breadcrumb';
import { ParticleCanvas } from './components/ParticleCanvas';
import { WhatsAppButton } from './components/WhatsAppButton';
import { SearchModal } from './components/SearchModal';



const ProductDetailPage = React.lazy(() => import('./pages/ProductDetailPage').then(module => ({ default: module.ProductDetailPage })));
const ServiceDetailPage = React.lazy(() => import('./pages/ServiceDetailPage').then(module => ({ default: module.ServiceDetailPage })));
const HomePage = React.lazy(() => import('./pages/HomePage').then(module => ({ default: module.HomePage })));
const ServicesPage = React.lazy(() => import('./pages/ServicesPage').then(module => ({ default: module.ServicesPage })));
const ProductsPage = React.lazy(() => import('./pages/ProductsPage').then(module => ({ default: module.ProductsPage })));
const AboutPage = React.lazy(() => import('./pages/AboutPage').then(module => ({ default: module.AboutPage })));
const ContactPage = React.lazy(() => import('./pages/ContactPage').then(module => ({ default: module.ContactPage })));
const InsightsPage = React.lazy(() => import('./pages/InsightsPage').then(module => ({ default: module.InsightsPage })));
const MeetingSensePage = React.lazy(() => import('./pages/MeetingSensePage').then(module => ({ default: module.MeetingSensePage })));
const InventoryForecastingPage = React.lazy(() => import('./pages/InventoryForecastingPage').then(module => ({ default: module.InventoryForecastingPage })));
const EnvironmentalIntelligencePage = React.lazy(() => import('./pages/EnvironmentalIntelligencePage').then(module => ({ default: module.EnvironmentalIntelligencePage })));
const SolutionsPage = React.lazy(() => import('./pages/SolutionsPage').then(module => ({ default: module.SolutionsPage })));
const TechnologiesPage = React.lazy(() => import('./pages/TechnologiesPage').then(module => ({ default: module.TechnologiesPage })));
const PortfolioPage = React.lazy(() => import('./pages/PortfolioPage').then(module => ({ default: module.PortfolioPage })));
const ReAICoachPage = React.lazy(() => import('./pages/ReAICoachPage').then(module => ({ default: module.ReAICoachPage })));
const TechnologyDetailPage = React.lazy(() => import('./pages/TechnologyDetailPage').then(module => ({ default: module.TechnologyDetailPage })));
const IndustrySolutionsPage = React.lazy(() => import('./pages/IndustrySolutionsPage').then(module => ({ default: module.IndustrySolutionsPage })));
const AppSolutionsPage = React.lazy(() => import('./pages/AppSolutionsPage').then(module => ({ default: module.AppSolutionsPage })));
const SocialSolutionsPage = React.lazy(() => import('./pages/SocialSolutionsPage').then(module => ({ default: module.SocialSolutionsPage })));
const LegalPage = React.lazy(() => import('./pages/LegalPage').then(module => ({ default: module.LegalPage })));
const BlogPage = React.lazy(() => import('./pages/BlogPage').then(module => ({ default: module.BlogPage })));
const BlogPostPage = React.lazy(() => import('./pages/BlogPostPage').then(module => ({ default: module.BlogPostPage })));
const ThankYouPage = React.lazy(() => import('./pages/ThankYouPage').then(module => ({ default: module.ThankYouPage })));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage').then(module => ({ default: module.NotFoundPage })));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function App() {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  
  useEffect(() => {
    

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);




  return (
    <ThemeProvider defaultTheme="dark">
      <BrowserRouter>
      <ScrollToTop />
      <RouteMetaUpdater />
      <div className="min-h-screen bg-obsidian text-slate-900 dark:text-white flex flex-col font-sans selection:bg-royal-blue selection:text-white relative">
        {/* Ambient Particle Neural Canvas */}
        <ParticleCanvas />

        {/* Header */}
        <Header onOpenSearch={() => setIsSearchOpen(true)} />

        {/* Main View Page Routes */}
        <main className="flex-1 relative z-10 flex flex-col">
          <Breadcrumb />
          <Suspense fallback={<PageSkeleton />}>
            <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/:id" element={<ServiceDetailPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/reaicoach" element={<ReAICoachPage />} />
            <Route path="/products/:id" element={<ProductDetailPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/portfolio" element={<PortfolioPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/label/:labelSlug" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />

            {/* AI & Industry Solutions */}
            <Route path="/technology" element={<TechnologiesPage />} />
            <Route path="/technology/:id" element={<TechnologyDetailPage />} />
            <Route path="/solutions" element={<SolutionsPage />} />
            <Route path="/solutions/meetingsense" element={<MeetingSensePage />} />
            <Route path="/solutions/inventory-forecasting" element={<InventoryForecastingPage />} />
            <Route path="/solutions/environmental-intelligence" element={<EnvironmentalIntelligencePage />} />
            <Route path="/solutions/industry" element={<IndustrySolutionsPage />} />
            <Route path="/solutions/industry/:category" element={<IndustrySolutionsPage />} />
            <Route path="/solutions/app-development/:appType" element={<AppSolutionsPage />} />
            <Route path="/solutions/social/:service" element={<SocialSolutionsPage />} />

            {/* Legal */}
            <Route path="/privacy-policy" element={<LegalPage type="privacy" />} />
            <Route path="/terms-of-service" element={<LegalPage type="terms" />} />

            {/* Fallback */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
        </main>

        {/* Footer */}
        <Footer />

        {/* Search Modal */}
        <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
        
        {/* WhatsApp Floating Button */}
        <WhatsAppButton />

      </div>
    </BrowserRouter>
    </ThemeProvider>
  );
}
